import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { depotsAPI, companiesAPI } from '../services/api';
import DataTable from '../components/DataTable';
import {PageHeader} from '../components/Ui';
import { useAuthStore } from '../store/authStore';

export default function Depots() {
  const { user: currentUser } = useAuthStore();
  const [depots, setDepots] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    ruc: '',
    address: '',
    email: '',
    phone: '',
    latitude: 0,
    longitude: 0,
    company_id: currentUser?.company_id || 1,
  });

  useEffect(() => {
    fetchDepots();
    fetchCompanies();
  }, []);

  const fetchDepots = async () => {
    setLoading(true);
    try {
      const response = await depotsAPI.getAll();
      setDepots(response.data);
    } catch (error) {
      toast.error('Error fetching depots');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      // Use public endpoint for non-gods_eye users (cliente only has access to their own company)
      const response = currentUser?.user_type === 'gods_eye' 
        ? await companiesAPI.getAll()
        : await companiesAPI.getPublic();
      setCompanies(response.data);
      // Use current user's company, or first company if available
      const defaultCompanyId = currentUser?.company_id || (response.data.length > 0 ? response.data[0].id : 1);
      setFormData(prev => ({ 
        ...prev, 
        company_id: defaultCompanyId
      }));
    } catch (error) {
      console.error('Error fetching companies', error);
      toast.error('Error loading companies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate company_id is set
      if (!formData.company_id || formData.company_id === 0) {
        toast.error('Please select a company');
        return;
      }
      
      if (editingId) {
        console.log('Updating depot with payload:', formData);
        await depotsAPI.update(editingId, formData);
        toast.success('Depot updated successfully');
        setEditingId(null);
      } else {
        console.log('Creating depot with payload:', formData);
        await depotsAPI.create(formData);
        toast.success('Depot created successfully');
      }
      const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
      setFormData({ 
        name: '', 
        ruc: '', 
        address: '', 
        email: '', 
        phone: '',
        latitude: 0,
        longitude: 0,
        company_id: defaultCompanyId 
      });
      setShowForm(false);
      fetchDepots();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Error saving depot';
      toast.error(errorMsg);
      console.error('Submit error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this depot?')) {
      try {
        await depotsAPI.delete(id);
        toast.success('Depot deleted successfully');
        fetchDepots();
      } catch (error) {
        toast.error('Error deleting depot');
      }
    }
  };

  const handleEdit = (depot) => {
    const companyId = depot.company_id || currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      ...depot,
      company_id: companyId
    });
    setEditingId(depot.id);
    setShowForm(true);
  };

  const handleAddClick = () => {
    const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      name: '', 
      ruc: '', 
      address: '', 
      email: '', 
      phone: '',
      latitude: 0,
      longitude: 0,
      company_id: defaultCompanyId 
    });
    setEditingId(null);
    setShowForm(true);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'ruc', label: 'RUC', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    ...(currentUser?.user_type !== 'cliente' ? [{
      key: 'company_id',
      label: 'Company',
      render: (value) => getCompanyName(value),
      sortable: true
    }] : []),
  ];

  return (
    <div className="container">
      <PageHeader title="Depots" description="Manage pickup and delivery locations for your operations.">
      <button className="btn btn-primary" onClick={handleAddClick}>
        Add Depot
      </button>
      </PageHeader>

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({ name: '', ruc: '', address: '', email: '', phone: '', latitude: 0, longitude: 0, company_id: 1 });
            }}>&times;</span>
            <h2>{editingId ? 'Edit Depot' : 'Add Depot'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>RUC</label>
                <input
                  type="text"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  step="0.000001"
                  min="-90"
                  max="90"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  step="0.000001"
                  min="-180"
                  max="180"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                  disabled={currentUser?.user_type !== 'gods_eye'}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {currentUser?.user_type !== 'gods_eye' && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Your company is automatically selected
                  </small>
                )}
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          data={depots}
          columns={columns}
          searchableFields={['name', 'ruc', 'email', 'phone']}
          itemsPerPage={10}
          onEdit={handleEdit}
          onDelete={(depot) => handleDelete(depot.id)}
          title="Depots List"
        />
      )}
    </div>
  );
}
